package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.kaihuoy.shared.data.User
import com.kaihuoy.shared.data.InspectionRecord
import com.kaihuoy.shared.data.Machine
import com.kaihuoy.shared.data.Tyomaa
import com.kaihuoy.components.common.*
import com.kaihuoy.components.common.*
import com.kaihuoy.web.router.AppRouterState
import com.kaihuoy.web.FirebaseService
import kotlinx.browser.window
import kotlin.time.Clock
import dev.gitlive.firebase.database.DatabaseReference
import kotlinx.coroutines.flow.first

@Composable
fun NewInspectionScreen(user: User?) {
    var currentStep by remember { mutableStateOf(1) } // 1 = perustiedot, 2 = tarkastuslista, 3 = yhteenveto

    // Perustiedot
    var siteName by remember { mutableStateOf("") }
    var siteAddress by remember { mutableStateOf("") }
    var machineType by remember { mutableStateOf("") }
    var equipName by remember { mutableStateOf("") }
    var equipId by remember { mutableStateOf("") }
    var manufacturingYear by remember { mutableStateOf("") }
    var operatingHours by remember { mutableStateOf("") }
    var selectedMachineId by remember { mutableStateOf("") }
    var ownerName by remember { mutableStateOf("") }
    var ownerPhone by remember { mutableStateOf("") }
    var ownerEmail by remember { mutableStateOf("") }

    // Tarkastuslistat
    val inspectionItems1 = listOf(
        "1. Puomi/aisat", "2. Paineputket, -letkut ja sylinterit", "3. Letkurikkoventtiili",
        "4. Nostokoukku", "5. Nostoapuvälineet", "6. Kuormitustaulukko", "7. Akselistot ja vaihteisto",
        "8. Renkaat", "9. Jarrujärjestelmä", "9.1 - putket, letkut", "9.2 - ajojarrut (2-piirinen)",
        "9.3 - seisontajarru", "9.4 - telajarru", "9.5 - ylävaunun jarru", "10. Hallintalaitteet, polkimet, vivut",
        "11. Kuljettajan istuin", "12. Valot", "12.1 - ajovalot", "12.2 - työvalot", "13. Lämmityslaite"
    )
    val inspectionItems2 = listOf(
        "14. Suojakatos/ ohjaamo", "15. Tuulilasi ja pyyhkimet", "16. Liukuesteet",
        "17. Tukijalat ja -levyt", "18. Kauhan kunto", "19. Lavan kunto", "20. Merkinantolaitteet",
        "20.1 - äänimerkki", "20.2 - vilkku", "20.3 - peruutusvalo", "20.4 - peruutushälytin", "21. Varoituskilvet",
        "22. Moottorinpysäytin-laite (SEIS/STOP)", "23. Polttoainesäiliö ja -järjestelmä",
        "24. Pakoputkisto", "25. Käyttö- ja huolto-ohjeet", "26. Huoltopäiväkirja", "27. Tehdyt muut tarkastukset"
    )

    val checklistValues = remember { mutableStateMapOf<String, String>() }
    var repairNotes by remember { mutableStateOf("") }
    var driverName by remember { mutableStateOf(user?.displayName ?: "") }

    var machines by remember { mutableStateOf<List<Machine>>(emptyList()) }
    var tyomaat by remember { mutableStateOf<List<Tyomaa>>(emptyList()) }
    var isSaving by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        try {
            FirebaseService.database.reference("tyomaat").valueEvents.collect { snapshot ->
                tyomaat = snapshot.children.mapNotNull { child ->
                    try {
                        val t = child.value<Tyomaa>()
                        if (t.id.isBlank()) t.copy(id = child.key ?: "") else t
                    } catch (e: Exception) { null }
                }.sortedBy { it.nimi }
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (e: Exception) {
            console.error("Työmaat haku epäonnistui", e)
        }
    }

    LaunchedEffect(Unit) {
        try {
            val snapshot = FirebaseService.database.reference("machines").valueEvents.first()
            machines = snapshot.children.mapNotNull { child ->
                try {
                    val machine = child.value<Machine>()
                    if (machine.id.isBlank()) machine.copy(id = child.key ?: "") else machine
                } catch (e: Exception) {
                    val rawValue = try { child.value<Any?>() } catch (ex: Exception) { "unavailable" }
                    console.error("Koneen haku epäonnistui: ${child.key}, data: ", rawValue, e)
                    null
                }
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (e: Exception) {
            console.error("Koneiden haku epäonnistui", e)
            if (e.message?.contains("PERMISSION_DENIED") == true) {
                console.warn("Koneiden haku estetty Firebase-sääntöjen vuoksi. Varmista, että '/machines' on luettavissa kaikille kirjautuneille.")
            }
        }
    }

    fun resolveMachineHoursUpdate(): Pair<String, Long>? {
        val updatedHours = operatingHours.toLongOrNull()
            ?: throw IllegalArgumentException("Käyttötuntimäärän pitää olla numero.")

        val machineToUpdate = when {
            selectedMachineId.isNotBlank() -> machines.firstOrNull { it.id == selectedMachineId }
            else -> machines.firstOrNull { machine ->
                machine.id.isNotBlank() && (
                    machine.machineNumber.equals(equipId, ignoreCase = true) ||
                    machine.serialNumber.equals(equipId, ignoreCase = true)
                )
            }
        } ?: return null

        if (updatedHours < machineToUpdate.currentHours) {
            throw IllegalArgumentException("Käyttötunnit eivät voi olla pienemmät kuin koneen nykyiset tunnit (${machineToUpdate.currentHours}).")
        }

        return machineToUpdate.id to updatedHours
    }

    suspend fun updateMachineHoursFromInspection(machineId: String, updatedHours: Long) {
        FirebaseService.database.reference("machines")
            .child(machineId)
            .child("currentHours")
            .setValue(updatedHours)
    }

    suspend fun saveInspection() {
        val uid = user?.uid ?: return
        isSaving = true
        try {
            val machineHoursUpdate = resolveMachineHoursUpdate()
            val ref = FirebaseService.database.reference("inspections")
            val newRef = ref.push()
            val record = InspectionRecord(
                id = newRef.key ?: "",
                userName = user.displayName,
                siteName = siteName,
                siteAddress = siteAddress,
                machineType = machineType,
                machineManufacturerModel = equipName,
                serialNumber = equipId,
                manufacturingYear = manufacturingYear,
                operatingHours = operatingHours,
                ownerName = ownerName,
                phone = ownerPhone,
                email = ownerEmail,
                checklist = (inspectionItems1 + inspectionItems2).associateWith { 
                    checklistValues[it] ?: "" 
                }.mapKeys { it.key.replace(Regex("[.#$/\\[\\]]"), " ") },
                repairNotes = repairNotes,
                driverName = driverName,
                timestamp = Clock.System.now().toEpochMilliseconds(),
                userId = uid
            )
            machineHoursUpdate?.let { (machineId, updatedHours) ->
                updateMachineHoursFromInspection(machineId, updatedHours)
            }
            newRef.setValue(record)

            com.kaihuoy.components.common.AppFeedback.showSuccess("Tarkastus tallennettu onnistuneesti!")
            AppRouterState.navigate("hallinta")
        } catch (e: Exception) {
            com.kaihuoy.components.common.AppFeedback.showError("Tallennus epäonnistui: ${e.message}")
        } finally {
            isSaving = false
        }
    }

    Div(attrs = {
        style {
            padding(40.px, 20.px)
            maxWidth(1000.px)
            property("margin", "0 auto")
            backgroundColor(KaihuTheme.Background)
            minHeight(100.vh)
        }
    }) {
        Div(attrs = {
            style {
                backgroundColor(KaihuTheme.Surface)
                padding(32.px)
                borderRadius(16.px)
                property("box-shadow", KaihuTheme.ShadowMd)
            }
        }) {
            H1(attrs = { style { marginTop(0.px); marginBottom(8.px); color(KaihuTheme.TextPrimary) } }) { Text("Kaluston tarkastus") }
            P(attrs = { style { color(KaihuTheme.TextSecondary); marginBottom(32.px); fontSize(1.1.em) } }) {
                Text("Vaihe $currentStep / 3")
            }

            // ===== VAIHE 1: Perustiedot =====
            if (currentStep == 1) {
                H3(attrs = { style { marginBottom(24.px); property("border-bottom", "2px solid ${KaihuTheme.PrimaryTeal}"); paddingBottom(8.px); color(KaihuTheme.TextPrimary) } }) {
                    Text("1. Perustiedot") 
                }
                
                Div(attrs = { style { marginBottom(24.px); padding(20.px); backgroundColor(KaihuTheme.Background); borderRadius(12.px); property("border", "1px solid ${KaihuTheme.Divider}") } }) {
                    StyledDropdown(
                        value = machines.find { it.id == selectedMachineId }?.machineNumber ?: "",
                        options = listOf(DropdownOption("", "--- Valitse kone ---")) + machines.map { m ->
                            DropdownOption(m.machineNumber, "${m.machineNumber} - ${m.manufacturer} ${m.model}")
                        },
                        placeholder = "--- Valitse kone ---",
                        label = "Valitse kone listasta",
                        onSelect = { num ->
                            val machine = machines.find { it.machineNumber == num }
                            if (machine != null) {
                                selectedMachineId = machine.id
                                equipName = "${machine.manufacturer} ${machine.model}"
                                equipId = machine.machineNumber
                                manufacturingYear = machine.manufacturingYear
                                operatingHours = machine.currentHours.toString()
                            }
                        }
                    )
                }

                if (tyomaat.isNotEmpty()) {
                    Div(attrs = { style { marginBottom(20.px) } }) {
                        StyledDropdown(
                            value = tyomaat.find { it.nimi == siteName }?.id ?: "",
                            options = listOf(DropdownOption("", "— Valitse työmaa listasta —")) + tyomaat.map { t ->
                                DropdownOption(t.id, "${t.nimi}${if (t.osoite.isNotBlank()) " (${t.osoite})" else ""}")
                            },
                            placeholder = "— Valitse työmaa listasta —",
                            label = "Työmaa",
                            onSelect = { id ->
                                val t = tyomaat.find { it.id == id }
                                if (t != null) {
                                    siteName = t.nimi
                                    siteAddress = t.osoite
                                } else {
                                    siteName = ""
                                    siteAddress = ""
                                }
                            }
                        )
                    }
                }

                Div(attrs = {
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fit, minmax(min(300px, 100%), 1fr))")
                        gap(20.px)
                    }
                }) {
                    StyledInput(label = "Työmaa *", value = siteName, onValueChange = { siteName = it })
                    StyledInput(label = "Työmaan osoite", value = siteAddress, onValueChange = { siteAddress = it })
                    StyledInput(label = "Koneen merkki ja malli *", value = equipName, onValueChange = { equipName = it })
                    StyledInput(label = "Valmistusnumero / Koneen ID", value = equipId, onValueChange = { equipId = it })
                    StyledInput(label = "Valmistusvuosi", value = manufacturingYear, onValueChange = { manufacturingYear = it }, placeholder = "VVVV")
                    StyledInput(label = "Käyttötuntimäärä", value = operatingHours, onValueChange = { operatingHours = it })
                    StyledInput(label = "Omistajan nimi", value = ownerName, onValueChange = { ownerName = it })
                    StyledInput(label = "Puhelinnumero", value = ownerPhone, onValueChange = { ownerPhone = it })
                    StyledInput(label = "Sähköposti", value = ownerEmail, onValueChange = { ownerEmail = it })
                }

                Div(attrs = { style { marginTop(40.px); display(DisplayStyle.Flex); gap(16.px); property("border-top", "1px solid ${KaihuTheme.Divider}"); paddingTop(24.px) } }) {
                    StyledButton(text = "Seuraava →", onClick = {
                        if (siteName.isBlank() || equipName.isBlank()) {
                            com.kaihuoy.components.common.AppFeedback.showWarning("Työmaa ja koneen tiedot ovat pakollisia")
                        } else {
                            currentStep = 2
                            window.scrollTo(0.0, 0.0)
                        }
                    })
                }
            }

            // ===== VAIHE 2: Tarkastuslista (Kaksi saraketta kuten kuvassa) =====
            else if (currentStep == 2) {
                H3(attrs = { style { marginBottom(24.px); property("border-bottom", "2px solid ${KaihuTheme.PrimaryTeal}"); paddingBottom(8.px); color(KaihuTheme.TextPrimary) } }) {
                    Text("2. Tarkastuslista") 
                }

                Div(attrs = {
                    style {
                        display(DisplayStyle.Grid)
                        property("grid-template-columns", "repeat(auto-fit, minmax(min(400px, 100%), 1fr))")
                        gap(24.px)
                        alignItems(AlignItems.FlexStart)
                    }
                }) {
                    // Vasen sarake (kohdat 1-13)
                    Div {
                        inspectionItems1.forEach { item ->
                            InspectionRow(item, checklistValues[item] ?: "", { checklistValues[item] = it })
                        }
                    }
                    
                    // Oikea sarake (kohdat 14-27)
                    Div {
                        inspectionItems2.forEach { item ->
                            InspectionRow(item, checklistValues[item] ?: "", { checklistValues[item] = it })
                        }
                    }
                }

                Div(attrs = { style { marginTop(40.px); display(DisplayStyle.Flex); gap(16.px); property("border-top", "1px solid ${KaihuTheme.Divider}"); paddingTop(24.px) } }) {
                    StyledButton(text = "← Edellinen", onClick = { currentStep--; window.scrollTo(0.0, 0.0) }, backgroundColor = KaihuTheme.Grey)
                    StyledButton(text = "Seuraava (Yhteenveto) →", onClick = { currentStep++; window.scrollTo(0.0, 0.0) })
                }
            }

            // ===== VAIHE 3: Yhteenveto =====
            else if (currentStep == 3) {
                H3(attrs = { style { marginBottom(24.px); property("border-bottom", "2px solid ${KaihuTheme.PrimaryTeal}"); paddingBottom(8.px); color(KaihuTheme.TextPrimary) } }) {
                    Text("3. Yhteenveto ja allekirjoitus") 
                }
                
                Div(attrs = { style { padding(24.px); backgroundColor(KaihuTheme.Background); borderRadius(12.px); marginBottom(32.px); property("border", "1px solid ${KaihuTheme.Divider}") } }) {
                    P(attrs = { style { fontWeight("600"); color(KaihuTheme.TextPrimary) } }) { Text("Kone: $equipName ($equipId)") }
                    P(attrs = { style { color(KaihuTheme.TextSecondary) } }) { Text("Työmaa: $siteName") }
                }

                H4 { Text("Tarkempi erittely korjattavista kohteista") }
                TextArea(
                    value = repairNotes,
                    attrs = {
                        style {
                            width(100.percent)
                            minHeight(120.px)
                            padding(12.px)
                            borderRadius(8.px)
                            property("border", "1px solid ${KaihuTheme.Border}")
                            backgroundColor(KaihuTheme.Surface)
                            outline("none")
                            marginBottom(24.px)
                            property("resize", "vertical")
                        }
                        attr("placeholder", "Kirjoita täytettävät huolto- ja korjaustarpeet...")
                        onInput { repairNotes = it.value }
                    }
                )

                StyledInput(label = "Tarkastajan / Kuljettajan nimi *", value = driverName, onValueChange = { driverName = it })

                Div(attrs = { style { marginTop(40.px); display(DisplayStyle.Flex); gap(16.px); property("border-top", "1px solid ${KaihuTheme.Divider}"); paddingTop(24.px) } }) {
                    StyledButton(text = "← Edellinen", onClick = { currentStep = 2; window.scrollTo(0.0, 0.0) }, backgroundColor = KaihuTheme.Grey)
                    StyledButton(
                        text = if (isSaving) "Tallennetaan..." else "Tallenna tarkastus",
                        icon = if (isSaving) null else "circle-check",
                        enabled = !isSaving,
                        onClick = {
                            if (driverName.isBlank()) {
                                com.kaihuoy.components.common.AppFeedback.showWarning("Tarkastajan nimi on pakollinen")
                            } else {
                                scope.launch { saveInspection() }
                            }
                        },
                        backgroundColor = KaihuTheme.Success
                    )
                }
            }
        }
    }
}

@Composable
private fun InspectionRow(label: String, value: String, onValueChange: (String) -> Unit) {
    val isSubNumber = label.split(" ").firstOrNull()?.replace(".", ",")?.replace(",", ".")?.toDoubleOrNull()?.let { it % 1.0 != 0.0 } ?: false
    val isSubItem = label.contains("- ") || label.startsWith("- ") || isSubNumber
    val isMainCategory = (label.startsWith("9. ") || label.startsWith("12. ") || label.startsWith("20. ") || 
                        label.startsWith("9 ") || label.startsWith("12 ") || label.startsWith("20 ")) && !isSubNumber ||
                        label.contains("Jarrujärjestelmä") || label.contains("Valot") || label.contains("Merkinantolaitteet")
    
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            alignItems(AlignItems.Center)
            padding(12.px, 16.px)
            marginBottom(8.px)
            backgroundColor(if (isMainCategory) Color("#0d1f26") else KaihuTheme.Surface)
            borderRadius(8.px)
            if (isSubItem) property("margin-left", "24.px")
            if (isMainCategory) {
                property("border-left", "4px solid ${KaihuTheme.PrimaryTeal}")
                marginTop(12.px)
                property("box-shadow", "0 2px 8px rgba(34,211,238,0.08)")
            } else {
                property("border-bottom", "1px solid ${KaihuTheme.Divider}")
            }
            property("transition", "all 0.2s ease")
        }
    }) {
        Span(attrs = { 
            style { 
                fontWeight(if (isMainCategory) "800" else if (isSubItem) "normal" else "600")
                color(if (isMainCategory) KaihuTheme.PrimaryTeal else KaihuTheme.TextPrimary)
                fontSize(if (isMainCategory) 1.05.em else if (isSubItem) 0.9.em else 0.95.em)
                flex(1)
                paddingRight(12.px)
            } 
        }) { Text(label) }
        
        if (!isMainCategory) {
            Div(attrs = { 
                style { 
                    display(DisplayStyle.Flex)
                    gap(12.px)
                    flexWrap(FlexWrap.Wrap)
                    justifyContent(JustifyContent.FlexEnd)
                } 
            }) {
                listOf("Kunnossa", "Korjattava", "Huom.").forEach { status ->
                    val isSelected = value == status
                    val statusColor = when(status) {
                        "Kunnossa" -> KaihuTheme.Success
                        "Korjattava" -> KaihuTheme.Danger
                        else -> KaihuTheme.Warning
                    }
                    
                    Div(attrs = {
                        onClick { onValueChange(status) }
                        style {
                            padding(8.px, 12.px)
                            borderRadius(8.px)
                            cursor("pointer")
                            fontSize(0.85.em)
                            fontWeight("600")
                            textAlign("center")
                            minWidth(80.px)
                            
                            backgroundColor(if (isSelected) statusColor else KaihuTheme.Surface)
                            color(if (isSelected) Color.white else KaihuTheme.TextSecondary)
                            property("border", "2px solid ${if (isSelected) statusColor else KaihuTheme.Border}")
                            property("transition", "all 0.2s ease")
                            
                            if (isSelected) {
                                property("box-shadow", "0 2px 4px rgba(0,0,0,0.1)")
                            }
                        }
                    }) { 
                        Text(status.uppercase())
                    }
                }
            }
        }
    }
}
